import { useId, useState } from 'react';
import kebabCase from 'lodash/kebabCase';

import { Button } from '@simplywallst/ui-core';
import ChevronRightSmallSolid from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';
import { Truncator } from '@/components/Truncator';
import { useDebounce } from 'use-debounce';

type Props = {
  title: string;
  children: React.ReactNode;
};

export const Accordion = ({ title, children }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const panelId = useId();
  const panelTitleId = useId();
  // When accordion is closed, we don't need up-to-date children,
  // however occassional refresh will allow correct measurements
  // of the height for Truncator to animate expand/collapse properly
  const [debouncedChildren] = useDebounce(children, 2000);

  return (
    <div className="grid rounded-x1 bg-p-grey-50">
      <Button
        styleType="passive"
        id={panelTitleId}
        onClick={toggleAccordion}
        data-cy-id={`${kebabCase(title)}-accordion`}
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-controls={panelId}
        // TODO TW - Remove important when we have TW button that can be extended
        className="!h-auto !p-x2"
      >
        <div className="grid grid-cols-[auto_min-content] items-center">
          <div className="text-left text-xs text-softer">{title}</div>

          {isOpen ? (
            <ChevronDownSmallSolid className="size-x3 fill-soft" />
          ) : (
            <ChevronRightSmallSolid className="size-x3 fill-soft" />
          )}
        </div>
      </Button>

      <Truncator open={isOpen} minHeight={0} animationDuration={0.3}>
        <div
          id={panelId}
          role="region"
          aria-labelledby={panelTitleId}
          aria-hidden={isOpen ? 'false' : 'true'}
        >
          {isOpen ? children : debouncedChildren}
        </div>
      </Truncator>
    </div>
  );
};
